import dayjs from 'dayjs'
import * as R from 'ramda'

const toCloseScheduleMoment = (scheduleDate, scheduleTime) =>
  scheduleDate?.hour(scheduleTime).minute(0).second(0).millisecond(0)

export const isCloseScheduleInThePast = (scheduleDate, scheduleTime) => {
  if (R.isNil(scheduleDate) || R.isNil(scheduleTime)) {
    return true
  }
  return !toCloseScheduleMoment(scheduleDate, scheduleTime).isAfter(dayjs())
}

export const getCloseScheduleTimestamp = (scheduleDate, scheduleTime) =>
  toCloseScheduleMoment(scheduleDate, scheduleTime)?.toISOString()

export const checkHttpPrefix = /^https?:\/\/[\w.-][\w-]*\.[\w.-][\w.~:/?#[\]@!$&'()*+,;=%-]+$/i

export const MAIL_TO_REGEX = /^mailto:[!\s]*/

/**
 * Normalises a user-supplied URL into a consistent https:// form.
 * - Trims surrounding whitespace
 * - mailto: links are returned unchanged
 * - http:// and https:// links are returned unchanged
 * - Bare hostnames (e.g. example.com) get https:// prepended
 */
export function normalizeUrl(input) {
  const trimmed = (input || '').trim()
  if (!trimmed || MAIL_TO_REGEX.test(trimmed)) {
    return trimmed
  }
  if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
    return 'https://' + trimmed
  }
  return trimmed
}

// FIFTH implementation of the {{ticket.<flat-key>}} rule (D11, D19). The others:
// web-survey/src/lib/resolveMergeTags/index.js, its byte-identical web-embed twin,
// service-survey/service_survey/merge_tags.py, and the gateway serializer's copy.
// No shared module reaches all five — the canonical 12-form case list on the
// PET-2181 breakdown is the only thing tying them together, and every divergence
// defect this feature produced came from two copies disagreeing.
//
// Trim the whole expression, split on the FIRST dot, strip each side, match the
// namespace case-insensitively, require a non-empty key with no further dots.
const parseTicketMergeTag = (inner) => {
  const trimmed = inner.trim()
  const dotIndex = trimmed.indexOf('.')
  if (dotIndex === -1) {
    return null
  }
  const namespace = trimmed.slice(0, dotIndex).trim()
  const key = trimmed.slice(dotIndex + 1).trim()
  if (!/^ticket$/i.test(namespace)) {
    return null
  }
  if (!key || key.includes('.')) {
    return null
  }
  return key
}

// The regex literal is INLINE on purpose. A module-level /g/ regex would carry
// lastIndex between calls, which is the statefulness `checkHttpPrefix`'s dead `gm`
// is deliberately not given. A literal is a fresh object per evaluation.
export const holdsTicketMergeTag = (value) => {
  if (typeof value !== 'string') {
    return false
  }
  const tags = value.match(/\{\{([^{}]*)\}\}/g) || []
  return tags.some((raw) => parseTicketMergeTag(raw.slice(2, -2)) !== null)
}

const changedTextFields = (button, storedButton) =>
  ['link', 'label'].filter((field) => R.prop(field, button) !== R.prop(field, storedButton))

/**
 * Returns a copy with `normalizeUrl` applied to every changed, NON-TEMPLATE link.
 *
 * TS5 — repairs 76 of the 143 bad links in production the next time an admin saves
 * (`www.google.com` becomes `https://www.google.com`, so the save stops failing).
 * TS6 — must NEVER touch a template. `normalizeUrl` prepends `https://` to anything
 * without a scheme, so running it on `{{ticket.url}}` would silently rewrite it to
 * `https://{{ticket.url}}` and break every merge tag an admin saves.
 */
export const normalizeCallToActionButtonLinks = (callToActionButtons, previousButtons = {}) => {
  const normalized = {}

  for (const sentiment in callToActionButtons) {
    const buttonBySentiment = callToActionButtons[sentiment]
    if (!buttonBySentiment || typeof buttonBySentiment !== 'object') {
      normalized[sentiment] = buttonBySentiment
      continue
    }

    normalized[sentiment] = {}
    for (const buttonType in buttonBySentiment) {
      const button = buttonBySentiment[buttonType]
      const storedButton = R.pathOr({}, [sentiment, buttonType], previousButtons)
      const link = R.prop('link', button)
      const shouldNormalize =
        button &&
        typeof button === 'object' &&
        typeof link === 'string' &&
        link !== R.prop('link', storedButton) &&
        !holdsTicketMergeTag(link)

      normalized[sentiment][buttonType] = shouldNormalize
        ? { ...button, link: normalizeUrl(link) }
        : button
    }
  }

  return normalized
}

const TICKET_URL_PLACEHOLDER_PATTERN = /\{\{\s*(?:ticket_id|customer_id)\s*\}\}/g

export const hasInvalidTicketUrlPlaceholder = (ticketUrlPrefix) => {
  if (!ticketUrlPrefix) {
    return false
  }

  const remainder = ticketUrlPrefix.replace(TICKET_URL_PLACEHOLDER_PATTERN, '')
  return remainder.includes('{') || remainder.includes('}')
}

// The three failure modes a save can now hit. One string cannot be right for all
// three: telling an admin "links with variables need https://" when the real
// problem is that variables are not enabled for their workspace is actively
// misleading, and the current copy on develop names the https/mailto rule even
// when the blocker is braces.
export const CTA_VALIDATION_ERRORS = {
  INVALID_LINK: 'INVALID_LINK',
  TEMPLATE_NOT_HTTPS: 'TEMPLATE_NOT_HTTPS',
  FEATURE_DISABLED: 'FEATURE_DISABLED',
}

const getChangedFields = (button, storedButton) => {
  const hasBecomeActive = !R.prop('isActive', storedButton) && R.prop('isActive', button)
  return hasBecomeActive ? ['link', 'label'] : changedTextFields(button, storedButton)
}

const getSingleButtonError = (button, storedButton, enableCtaButtonVariables) => {
  const changedFields = getChangedFields(button, storedButton)

  const hasTicketMergeTag = changedFields.some((field) =>
    holdsTicketMergeTag(R.prop(field, button))
  )
  if (hasTicketMergeTag && !enableCtaButtonVariables) {
    return CTA_VALIDATION_ERRORS.FEATURE_DISABLED
  }

  if (!changedFields.includes('link')) {
    return null
  }

  const buttonLink = R.prop('link', button)

  if (holdsTicketMergeTag(buttonLink)) {
    return buttonLink.startsWith('https://') ? null : CTA_VALIDATION_ERRORS.TEMPLATE_NOT_HTTPS
  }

  const isValidLink = checkHttpPrefix.test(buttonLink) || MAIL_TO_REGEX.test(buttonLink)
  return isValidLink ? null : CTA_VALIDATION_ERRORS.INVALID_LINK
}

/**
 * Returns the first failure reason, or null when the config is savable.
 *
 * @param {object} callToActionButtons - the config about to be saved
 * @param {object} [options]
 * @param {object} [options.previousButtons] - the last SAVED config, for changed-only
 *   diffing (D16). Omitted means "treat everything as changed", which is exactly the
 *   pre-PET-2181 behaviour — that is why the existing tests still hold.
 * @param {boolean} [options.enableCtaButtonVariables] - enable_cta_button_variables
 *   for this company. Resolved from the same gateway endpoint the serializer resolves
 *   from, so the two gates cannot disagree.
 */
export const getCallToActionButtonsError = (callToActionButtons, options = {}) => {
  const { previousButtons = {}, enableCtaButtonVariables = false } = options

  for (const sentiment in callToActionButtons) {
    const buttonBySentiment = callToActionButtons[sentiment]

    for (const buttonType in buttonBySentiment) {
      const button = buttonBySentiment[buttonType]
      if (!R.prop('isActive', button)) {
        continue
      }

      const storedButton = R.pathOr({}, [sentiment, buttonType], previousButtons)
      const error = getSingleButtonError(button, storedButton, enableCtaButtonVariables)
      if (error) {
        return error
      }
    }
  }

  return null
}

/** Boolean form, kept so existing callers and tests are unaffected. */
export const validateCallToActionButtons = (callToActionButtons, options = {}) =>
  getCallToActionButtonsError(callToActionButtons, options) === null



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/organisms/SurveyConfiguration/utils.js